import { useCallback, useRef, useState } from 'react';
import { DropdownProps } from 'react-day-picker';
import { twMerge } from 'tailwind-merge';
import Check from '../../../assets/icons/General/check.svg?react';
import { useOutsideClick } from '../../../utilities/common/hooks/useOutsideClick';
import { Typography } from '../Typography';
import { ChevronBottomIcon } from '../../../assets/icons/ChevronBottomIcon';

export default function CustomDayPickerDropdown({
  options,
  value,
  onChange,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options?.find((opt) => opt.value === value);

  const handleSelect = (optionValue: number) => {
    onChange?.({
      target: { value: String(optionValue) },
    } as React.ChangeEvent<HTMLSelectElement>);
    setIsOpen(false);
  };

  useOutsideClick(
    containerRef,
    useCallback(() => setIsOpen(false), []),
    isOpen,
  );

  const getOptionClassName = (
    isDisabled: boolean,
    isSelected: boolean,
  ): string => {
    return twMerge(
      'flex w-full cursor-pointer items-center justify-between px-3 py-2 text-base transition-colors text-grey-800 hover:bg-grey-50',
      isDisabled && 'text-grey-400 cursor-not-allowed',
      isSelected && 'bg-grey-50 font-medium',
    );
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="border-grey-100 flex w-full cursor-pointer flex-row items-center justify-between gap-5 rounded-xl border px-3 py-2"
      >
        <Typography
          as="span"
          className="text-grey-800 font-normal"
          variant="body-md"
        >
          {selectedOption?.label}
        </Typography>
        <ChevronBottomIcon
          className={`h-3 w-3 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <ul
          role="listbox"
          className="border-grey-100 absolute top-full z-10 mt-1 max-h-60 min-w-40 overflow-y-auto rounded-xl border bg-white p-1 shadow-lg"
        >
          {options?.map((opt) => (
            <button
              key={opt.value}
              id={`option-${opt.value}`}
              role="option"
              aria-selected={opt.value === value}
              aria-disabled={opt.disabled}
              onClick={() => !opt.disabled && handleSelect(opt.value)}
              className={getOptionClassName(
                opt.disabled || false,
                opt.value === value,
              )}
            >
              <span>{opt.label}</span>
              {opt.value === value && (
                <Check className="h-4 w-4 text-blue-700" />
              )}
            </button>
          ))}
        </ul>
      )}
    </div>
  );
}
